<!-- **** --------------------------------------------------------------------------
	SHARED SCRIPT
-------------------------------------------------------------------------- **** -->
<script lang="ts">
    import { computed, ComputedRef, onMounted, reactive, ref, toRefs, watch, watchEffect } from 'vue';
    import { bitshift, requestFrame, cancelFrame } from '@utils';

    // temp
    import vm from '@journeys/Journeys.viewmodel';
    const { canvas, viewport }: any = vm;

    const component = {
        name: 'Scrollbar3D', // math for depth on the z axis
    };
    export default {
        name: component.name,
    };
</script>

<!-- **** --------------------------------------------------------------------------
	SCRIPT
-------------------------------------------------------------------------- **** -->
<script lang="ts" setup>
    const props = defineProps<{
            placement: string;
            offset?: string;
            scrollSize: number;
            scrollMin: number;
            containerSize: number;
            nav: {
                [key: string]: number | any;
            };
            axis: string;
        }>(),
        { nav, axis, placement } = props,
        state = reactive({
            busy: true,
        });

    onMounted(() => {
        requestFrame(() => {
            state.busy = false;
        });
    });

    // TODO: add to props with defaults
    const gutterWidth: number = 18,
        handleWidth: number = 8 || gutterWidth;

    const sm = computed(() => props.scrollMin + props.containerSize / 2);

    const withDistance = (val: number) => (val * nav.p) / (nav.p - nav.z),
        handleOffset = computed(
            () =>
                (withDistance(nav[axis] + nav.z * (props.containerSize / nav.p / 2)) / withDistance(props.scrollSize)) * props.containerSize -
                (props.containerSize / props.scrollSize) * sm.value
        ),
        handleSize = computed(() => (props.containerSize / withDistance(props.scrollSize)) * props.containerSize),
        targetDim = /top|bottom/.test(props.placement) ? 'width' : 'height';

    let frameReq: number = 0,
        lv: {
            [key: string]: number;
        };

    // let editorScrollActive = ref(false),
    //    fadeTimer: NodeJS.Timeout
    // watch(nav, (val: any) => {
    //    editorScrollActive.value = true
    //    clearTimeout(fadeTimer)
    //    fadeTimer = setTimeout(()=>editorScrollActive.value = false,700)

    // })

    const handleScroll = (ev: any): void => {
        ev.preventDefault();
        ev.stopPropagation();

        cancelFrame(frameReq);
        frameReq = requestFrame(() => {
            // TODO: dry
            if (handleOffset.value >= 0 && handleOffset.value + handleSize.value <= props.containerSize)
                nav[axis] = bitshift.round(
                    ((((lv.handleOffset + ev[axis] - lv[axis]) / nav.p) * (nav.p - nav.z)) / props.containerSize) * mod - mod2 + sm.value
                );

            if (handleOffset.value < 0) nav[axis] = scroll_min;

            if (handleOffset.value + handleSize.value > props.containerSize)
                nav[axis] = bitshift.round(
                    ((((props.containerSize - handleSize.value) / nav.p) * (nav.p - nav.z)) / props.containerSize) * mod - mod2 + sm.value - 1
                );
        });
    };

    let mod = (props.scrollSize / (nav.p - nav.z)) * nav.p,
        mod2 = nav.z * (props.containerSize / nav.p / 2),
        scroll_min = props.scrollMin + nav.zscale_up(props.containerSize / 2) + 1;
    const scrollStart = (ev: any) => {
        state.busy = true;
        canvas.el?.setPointerCapture(ev.pointerId);

        lv = {
            [axis]: ev[axis],
            handleOffset: bitshift.round(handleOffset.value),
        };

        mod = (props.scrollSize / (nav.p - nav.z)) * nav.p;

        mod2 = nav.z * (props.containerSize / nav.p / 2);

        scroll_min = props.scrollMin + nav.zscale_up(props.containerSize / 2) + 1;

        document.addEventListener('pointermove', handleScroll);
        document.addEventListener(
            'pointerup',
            () => {
                state.busy = false;
                document.removeEventListener('pointermove', handleScroll);
            },
            { once: true }
        );
    };
</script>

<!-- **** --------------------------------------------------------------------------
	TEMPLATE
-------------------------------------------------------------------------- **** -->
<template>
    <div
        class="scrollbar-container"
        :class="targetDim === 'height' ? 'vertical' : 'horizontal'"
        :style="[
            {
                [placement]: offset || 0,
                [targetDim]: '100%',
            },
            `${/bottom|top/.test(placement) ? 'left' : 'top'}: 0`,
            `--gutter-width: ${gutterWidth}px;`,
            `--handle-width: ${handleWidth}px;`,
        ]">
        <div
            class="scrollbar-handle"
            :class="nav.active && 'opaque nav-active'"
            :style="[
                {
                    transform: `translate3d(${targetDim === 'width' ? handleOffset + 'px' : '-50%'}, ${targetDim === 'height' ? handleOffset + 'px' : '-50%'}, 0)`,
                    [targetDim]: `${handleSize - gutterWidth * 1.5}px`,
                },
                state.busy ? 'transition: none' : '',
            ]"
            @pointerdown.stop.prevent="scrollStart"></div>
    </div>
</template>

<!-- **** --------------------------------------------------------------------------
	STYLES
-------------------------------------------------------------------------- **** -->
<style>
    .scrollbar-container {
        --gutter-width: 18px;
        --handle-width: 9px;

        display: block;
        position: absolute;

        height: var(--gutter-width);
        width: var(--gutter-width);
        background-color: #c4c4c400;

        user-select: none;
        pointer-events: all;
        z-index: 20;
        overflow: hidden;
    }

    .scrollbar-handle {
        display: block;
        position: absolute;
        height: var(--handle-width);
        width: var(--handle-width);
        border-radius: var(--handle-width);
        background-color: #000000;
        /* background-color: #d1d1d1; */

        transition: transform var(--td-slow) var(--ttf-eager) 0.4s;
        transition-property: transform, height, width, background-color, opacity;
        /* will-change: background-color, transform, height, width; */
        /* transition-duration: .6s; */
        opacity: 0;
    }

    .prevent-scrollbar-opaque .scrollbar-handle {
        opacity: 0 !important;
    }
    .scrollbar-handle::before {
        content: '';
        position: absolute;
        top: calc(var(--gutter-width) * -0.5);
        left: calc(var(--gutter-width) * -0.5);
        height: calc(var(--gutter-width) * 1.5);
        width: calc(var(--gutter-width) * 1.5);
    }

    .scrollbar-container.vertical .scrollbar-handle {
        margin: calc(var(--gutter-width) * 0.7) 0;
        left: 50%;
    }
    .scrollbar-container.horizontal .scrollbar-handle {
        margin: 0 calc(var(--gutter-width) * 0.7);
        top: 50%;
    }
    .scrollbar-container.vertical .scrollbar-handle::before {
        top: 0;
        height: 100%;
    }
    .scrollbar-container.horizontal .scrollbar-handle::before {
        left: 0;
        width: 100%;
    }

    .scrollbar-container:active .scrollbar-handle,
    .scrollbar-container:hover .scrollbar-handle {
        opacity: 1;
        transition-delay: 0s;
        transition-duration: 0.2s;
    }

    .nav-active {
        opacity: 0.24;
        transition-delay: 0s;
        transition-duration: 0.4s;
    }
</style>
